<template>
  <HeadlineComponent>Schichtplan</HeadlineComponent>
  <div class="mb-5" v-if="events.length > 1">
    <label for="event_select">Event Auswählen</label>
    <select
      id="event_select"
      v-model="event_id"
      class="form-control"
      @change="updateEvent()"
    >
      <option>--- Auswählen ---</option>
      <option v-for="e of events" :key="e.id" :value="e.id">
        {{ e.name }}
      </option>
    </select>
  </div>
  <div
    class="alert alert-danger"
    role="alert"
    v-if="isAdmin && !has_crew_template && ticketing_enabled"
  >
    Es kann sich aktuell niemand in Schichten eintragen, weil kein CrewTemplate
    für das Event definiert wurde.
  </div>
  <div v-for="team of teams" :key="team.id">
    <h2>
      {{ team.name }}<br />
      <small v-if="team.teamleads"
        ><span
          v-for="teamlead of team.teamleads"
          :key="teamlead.id"
          class="comma-after"
          >{{ teamlead.full_name }}</span
        ></small
      >
    </h2>
    <div
      v-html="team.description"
      class="quill-generated-text team-description"
      v-if="team.description"
    ></div>
    <div class="row" v-for="(task, i) of team.tasks" :key="task.id">
      <template v-if="i % 3 === 0">
        <div class="col-sm mt-3" v-for="y of [0, 1, 2]" :key="y">
          <template v-if="team.tasks[i + y]">
            <h3>{{ team.tasks[i + y].name }}</h3>
            <div
              v-if="team.tasks[i + y].description"
              v-html="team.tasks[i + y].description"
              class="quill-generated-text task-description"
            ></div>
            <table class="table">
              <tbody>
                <tr v-for="shift of team.tasks[i + y].shifts" :key="shift.id">
                  <td style="line-height: 0.8">
                    {{ $filters.formatDate(shift.start, "HH:mm") }} -
                    {{ $filters.formatDate(shift.end, "HH:mm") }}<br />
                    <small style="font-size: x-small"
                      >{{ $filters.formatDate(shift.start, "dd DD.MM.YYYY") }} -
                      {{
                        $filters.formatDate(shift.end, "dd DD.MM.YYYY")
                      }}</small
                    >
                  </td>
                  <td v-if="shift.user" style="text-align: right">
                    <span
                      v-if="
                        shift.reserved &&
                        (shift.reserved_first_name || shift.reserved.last_name)
                      "
                      >{{ shift.reserved_first_name }}
                      {{ shift.reserved_last_name }}.</span
                    >
                    <span
                      v-if="
                        shift.reserved &&
                        !shift.reserved_first_name &&
                        !shift.reserved.last_name
                      "
                      >Reserviert</span
                    >
                    <span v-if="!shift.reserved">{{
                      shift.user.full_name
                    }}</span>
                  </td>
                  <td v-if="!shift.user" style="text-align: right">
                    <LoadingButton
                      :disabled="team.tasks[i + y].locked"
                      @click="assign(shift, team.tasks[i + y])"
                      class="btn-success btn-sm"
                      :loading="shift.loading"
                      ><span v-if="team.tasks[i + y].locked"
                        ><span class="fa fa-lock"></span> Gesperrt</span
                      >
                      <span v-if="!team.tasks[i + y].locked"
                        >Eintragen</span
                      ></LoadingButton
                    >
                  </td>
                </tr>
              </tbody>
            </table>
          </template>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import shiftService from "../services/shift.service.js";
import eventService from "../services/event.service";
import { mapActions, mapState } from "pinia";
import { useAlertsStore } from "../stores/alerts.js";
import { useUserStore } from "../stores/user.js";
import { useConfigStore } from "../stores/config";
import LoadingButton from "../components/LoadingButton.vue";
import HeadlineComponent from "../components/HeadlineComponent.vue";
import * as Sentry from '@sentry/vue';

export default {
  name: "ShiftsView",
  components: { LoadingButton, HeadlineComponent },
  data() {
    return {
      teams: [],
      events: [],
      has_crew_template: false,
      event_id: "",
    };
  },
  computed: {
    ...mapState(useUserStore, ["isAdmin"]),
    ...mapState(useConfigStore, ["ticketing_enabled"]),
  },
  async beforeRouteEnter(to, from, next) {
    let teams = {};
    let events = [];
    try {
      let response = await eventService.index();
      events = response.data;
      if (events.length) {
        response = await shiftService.index(
          to.query.event_id || events[0].id,
          true
        );
        teams = response.data;
      }
    } catch (e) {
      if (e) {
        Sentry.captureException(e);
        useAlertsStore().add(
          "danger",
          "Es ist was unerwartetes schief gegangen.."
        );
        window.scrollTo(0, 0);
      }
    }
    next((vm) => {
      vm.teams = teams;
      vm.events = events;
      if (events.length) {
        if (to.query.event_id) {
          vm.event_id = to.query.event_id;
        } else {
          vm.event_id = events[0].id;
          vm.has_crew_template = !!events[0].crew_template;
        }
      }
    });
  },
  methods: {
    ...mapActions(useAlertsStore, { addAlert: "add" }),
    async updateEvent() {
      var response = await shiftService.index(this.event_id);
      this.teams = response.data;
      this.$router.replace({ query: { event_id: this.event_id } });
    },
    async assign(shift, task) {
      shift.loading = true;
      try {
        let response = await shiftService.assign(this.event_id, shift.id);
        for (let i = 0; i < task.shifts.length; i++) {
          if (task.shifts[i].id === shift.id) {
            task.shifts[i] = response.data;
            break;
          }
        }
        this.addAlert(
          "success",
          "Du hast dich erfolgreich in die Schicht eingetragen!"
        );
      } catch (e) {
        if (e) {
          if (e.response && e.response.data.message) {
            this.addAlert("danger", e.response.data.message);
            window.scrollTo(0, 0);
          } else {
            Sentry.captureException(e);
            this.addAlert(
              "danger",
              "Es ist was unerwartetes schief gegangen.."
            );
            window.scrollTo(0, 0);
          }
        }
      }
      shift.loading = false;
    },
  },
};
</script>

<style scoped>
td {
  height: 48px;
  vertical-align: middle;
}
</style>
